import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPartnerOffer,
  deactivatePartnerOffer,
  fetchPartnerOffers,
  publishPartnerOffer,
  submitPartnerOffer,
  updatePartnerOffer,
  withdrawPartnerOffer,
  type OfferFormPayload,
} from '../api/partner-offers.api';

export const partnerOffersQueryKey = (entityId: string) => ['partner-offers', entityId];

/** All of the caller's own offers, every lifecycle state included (FR-29). */
export function usePartnerOffers(entityId: string) {
  return useQuery({
    queryKey: partnerOffersQueryKey(entityId),
    queryFn: () => fetchPartnerOffers(entityId),
  });
}

function useInvalidateOnSettle(entityId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: partnerOffersQueryKey(entityId) });
}

export function useCreateOffer(entityId: string) {
  const invalidate = useInvalidateOnSettle(entityId);
  return useMutation({
    mutationFn: (payload: OfferFormPayload) => createPartnerOffer(entityId, payload),
    onSuccess: invalidate,
  });
}

export function useUpdateOffer(entityId: string, offerId: string) {
  const invalidate = useInvalidateOnSettle(entityId);
  return useMutation({
    mutationFn: (payload: OfferFormPayload) => updatePartnerOffer(entityId, offerId, payload),
    onSuccess: invalidate,
  });
}

export function useSubmitOffer(entityId: string) {
  const invalidate = useInvalidateOnSettle(entityId);
  return useMutation({
    mutationFn: (offerId: string) => submitPartnerOffer(entityId, offerId),
    onSuccess: invalidate,
  });
}

export function useWithdrawOffer(entityId: string) {
  const invalidate = useInvalidateOnSettle(entityId);
  return useMutation({
    mutationFn: (offerId: string) => withdrawPartnerOffer(entityId, offerId),
    onSuccess: invalidate,
  });
}

export function usePublishOffer(entityId: string) {
  const invalidate = useInvalidateOnSettle(entityId);
  return useMutation({
    mutationFn: (offerId: string) => publishPartnerOffer(entityId, offerId),
    onSuccess: invalidate,
  });
}

export function useDeactivateOffer(entityId: string) {
  const invalidate = useInvalidateOnSettle(entityId);
  return useMutation({
    mutationFn: (offerId: string) => deactivatePartnerOffer(entityId, offerId),
    onSuccess: invalidate,
  });
}
